import { useEffect } from 'react';
import type { Surah } from '@/types/domain';

/**
 * Per-route SEO injector for the /surah index page.
 *
 * On mount + whenever the surah list changes:
 *   - <title>, <meta name="description">
 *   - <link rel="canonical"> → https://alhudah.com/surah
 *   - Open Graph + Twitter card meta
 *   - JSON-LD: CollectionPage wrapping an ItemList of all 114 surah URLs.
 *     Gives Google a structured "this is an index of N items" signal so
 *     every surah page link-flows into the others through this hub.
 *
 * No external deps — same manual DOM upsert pattern as SurahSeo.
 */
export function SurahIndexSeo({ surahs }: { surahs: Surah[] }) {
  useEffect(() => {
    const url = 'https://alhudah.com/surah';
    const title =
      'All 114 Surahs of the Quran — Read Online with English & Urdu Translation · Alhudah';
    const description =
      'Browse all 114 surahs of the Quran in order. Read each chapter online ' +
      'with Arabic text, English (Sahih International) and Urdu (Jalandhari) ' +
      'translation, and audio recitation. Free, no account required.';

    document.title = title;
    upsertMeta('description', description);
    upsertLink('canonical', url);

    upsertMeta('og:title', 'All 114 Surahs of the Quran — English & Urdu Translation', 'property');
    upsertMeta('og:description', description, 'property');
    upsertMeta('og:url', url, 'property');
    upsertMeta('og:type', 'website', 'property');

    upsertMeta('twitter:title', 'All 114 Surahs of the Quran with Translation');
    upsertMeta('twitter:description', description);

    const itemListElement = surahs.map((s, idx) => ({
      '@type': 'ListItem',
      position: idx + 1,
      name: `Surah ${s.name_en}`,
      url: `https://alhudah.com/surah/${s.id}`,
    }));

    const jsonLd = {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: 'All 114 Surahs of the Quran',
      description,
      url,
      inLanguage: ['en', 'ur', 'ar'],
      isPartOf: {
        '@type': 'WebSite',
        name: 'Alhudah',
        url: 'https://alhudah.com/',
      },
      mainEntity: {
        '@type': 'ItemList',
        name: '114 Surahs of the Quran',
        numberOfItems: surahs.length,
        itemListOrder: 'https://schema.org/ItemListOrderAscending',
        itemListElement,
      },
    };
    upsertJsonLd('surah-index-jsonld', jsonLd);

    return () => {
      upsertLink('canonical', 'https://alhudah.com/');
    };
  }, [surahs]);

  return null;
}

// ----- DOM helpers (mirrors SurahSeo) -------------------------------

function upsertMeta(
  name: string,
  content: string,
  attr: 'name' | 'property' = 'name',
): void {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function upsertLink(rel: string, href: string): void {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

function upsertJsonLd(id: string, payload: unknown): void {
  let el = document.getElementById(id) as HTMLScriptElement | null;
  if (!el) {
    el = document.createElement('script');
    el.id = id;
    el.type = 'application/ld+json';
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(payload);
}
